import { ROSELoader } from './rose';

/**
 * @enum {Number}
 * @readonly
 */
export const CHANNEL_TYPE = {
  None: 1 << 0,
  Position: 1 << 1,
  Rotation: 1 << 2,
  Normal: 1 << 3,
  Alpha: 1 << 4,
  Uv1: 1 << 5,
  Uv2: 1 << 6,
  Uv3: 1 << 7,
  Uv4: 1 << 8,
  TexAnim: 1 << 9,
  Scale: 1 << 10
};

export class AnimationChannel {
  type: number;
  index: number;
  frames: any[] = [];

  constructor(type: number, index: number) {
    this.type = type;
    this.index = index;
  }
}

/**
 * @property {Number} fps
 * @property {Number} frameCount
 * @property {AnimationChannel[]} channels
 */
export class AnimationData {
  // Static references for backwards compatibility
  static CHANNEL_TYPE = CHANNEL_TYPE;
  static Channel = AnimationChannel;

  fps: number = 0;
  frameCount: number = 0;
  channels: AnimationChannel[] = [];
  eventFrames: number[] = [];
  frameEvents: number[] = [];
  interpInterval: number = 500;

  static load(path: any, callback: any) {
    ROSELoader.load(path, function(rh: any) {
      let channels: any, i: any, j: any, magic: any;
      const data = new AnimationData();

      magic = rh.readStrLen(7);
      rh.skip(1);

      if (magic !== 'ZMO0002') {
        throw 'Unexpected ZMO magic header ' + magic + ' in ' + path;
      }

      data.fps = rh.readUint32();
      data.frameCount = rh.readUint32();

      channels = rh.readUint32();
      for (i = 0; i < channels; ++i) {
        const type  = rh.readUint32();
        const index = rh.readUint32();
        data.channels.push(new AnimationChannel(type, index));
      }

      data.frameEvents = [];
      for (i = 0; i < data.frameCount; ++i) {
        data.frameEvents.push(0);

        for (j = 0; j < channels; ++j) {
          let frame;

          switch (data.channels[j].type) {
          case CHANNEL_TYPE.Position:
            frame = rh.readVector3().multiplyScalar(ZZ_SCALE_IN);
            break;
          case CHANNEL_TYPE.Rotation:
            frame = rh.readQuatwxyz();
            break;
          case CHANNEL_TYPE.Scale:
            frame = rh.readVector3();
            break;
          case CHANNEL_TYPE.Normal:
            frame = rh.readVector3();
            break;
          case CHANNEL_TYPE.Alpha:
            frame = rh.readFloat();
            break;
          case CHANNEL_TYPE.Uv1:
            frame = rh.readVector2();
            break;
          case CHANNEL_TYPE.Uv2:
            frame = rh.readVector2();
            break;
          case CHANNEL_TYPE.Uv3:
            frame = rh.readVector2();
            break;
          case CHANNEL_TYPE.Uv4:
            frame = rh.readVector2();
            break;
          case CHANNEL_TYPE.TexAnim:
            frame = rh.readFloat();
            break;
          default:
            throw 'Unexpected ZMO channel type ' + data.channels[j].type + ' in ' + path;
          }

          data.channels[j].frames.push(frame);
        }
      }

      let fileVersion = 1;

      rh.seek(-4);
      const extTag = rh.readStrLen(4);
      if (extTag === 'EZMO') {
        fileVersion = 2;
      } else if (extTag === '3ZMO') {
        fileVersion = 3;
      }

      if (fileVersion > 1) {
        rh.seek(-8);
        const extDataStart = rh.readUint32();
        rh.seek(extDataStart);

        if (fileVersion >= 2) {
          const eventFrameCount = rh.readUint16();
          if (eventFrameCount !== data.frameCount) {
            console.warn('Encountered extended ZMO with an inconsistent frame count.');
          }

          for (i = 0; i < eventFrameCount; ++i) {
            data.eventFrames[i] = rh.readInt16();
          }
        }

        if (fileVersion >= 3) {
          data.interpInterval = rh.readInt32();
        }
      }

      callback(data);
    });
  }
}
